


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import './Calls.css';

interface CallData {
  id: string;
  call_sid: string;
  call_status: string;
  from_number: string;
  to_number: string;
  direction: string;
  created_at: string;
  duration?: string;
  transcriptions?: any[];
}

const Calls: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Calls');
  const [calls, setCalls] = useState<CallData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchCalls();
  }, [navigate]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/calls');
      console.log('Calls API Response:', response.data);
      
      if (response.data.success) {
        setCalls(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load calls');
      }
    } catch (err: any) {
      console.error('Error fetching calls:', err);
      setError(err.response?.data?.message || 'Failed to load calls data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/${tab.toLowerCase()}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch(status?.toLowerCase()) {
      case 'completed': return '#2ecc71';
      case 'in-progress': return '#f39c12';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getDirectionIcon = (direction: string) => {
    return direction === 'inbound' ? '📞 Inbound' : '📤 Outbound';
  };

  if (!isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="calls-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="loading-container">Loading calls data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="calls-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <h1>Calls</h1>
        {error && <div className="error-message">{error}</div>}
        
        {calls.length === 0 && !error ? (
          <div className="no-data">No calls found</div>
        ) : (
          <div className="calls-table-container">
            <table className="calls-table">
              <thead>
                <tr>
                  <th>Call SID</th>
                  <th>From Number</th>
                  <th>To Number</th>
                  <th>Direction</th>
                  <th>Duration</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call) => (
                  <tr key={call.id}>
                    <td className="call-sid">{call.call_sid?.substring(0, 15)}...</td>
                    <td>{call.from_number}</td>
                    <td>{call.to_number}</td>
                    <td>
                      <span className="direction-badge">
                        {getDirectionIcon(call.direction)}
                      </span>
                    </td>
                    <td>
                      {call.transcriptions?.[0]?.recording_duration 
                        ? `${call.transcriptions[0].recording_duration} sec` 
                        : 'N/A'}
                    </td>
                    <td>{formatDate(call.created_at)}</td>
                    <td>
                      <span 
                        className="status-badge" 
                        style={{ background: getStatusColor(call.call_status) }}
                      >
                        {call.call_status || 'N/A'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Calls;